import {
  LogoThumb,
  PeaqueCreate,
  PeaqueEngine,
} from '../components/illustrations'
import type { CaseStudy } from './types'

export const peaque: CaseStudy = {
  id: 'Peaque',
  card: {
    id: 'Peaque',
    href: '/Peaque',
    image: '/assets/case-study/peaque/01.jpg',
    imageAlt: 'Peaque',
    title: 'Peaque',
    domain: 'peaque.io',
    miniDescription:
      'A social reading app - read books, leave notes on any line, celebrate the fandom.',
    caseNum: '04 - Reading - Social',
    lead: 'A new way to read - a full ebook reader where the fandom lives alongside the page. Highlight any line to leave a note, and the community upvotes, replies, and shares the best ones.',
    tags: [
      { label: 'Serverless EPUB engine', key: true },
      { label: 'Firebase' },
      { label: 'Highlight-to-note', key: true },
      { label: 'Upvotes · replies · shares' },
      { label: 'Full-text search' },
    ],
    outcomes: [
      'First-of-its-kind serverless EPUB engine - renders on demand',
      'Highlight any line to leave a note, public or private',
      'Read, create, and celebrate in one reader',
    ],
    thumb: (
      <LogoThumb
        src="/assets/case-study/peaque/peaque.svg"
        alt="Peaque"
        color="#3fb6a2"
      />
    ),
  },
  imageSizes: {
    '/assets/case-study/peaque/01.jpg': { width: 2152, height: 1343 },
    '/assets/case-study/peaque/02.webp': { width: 2160, height: 1350 },
    '/assets/case-study/peaque/04.webp': { width: 2160, height: 1349 },
  },
  content: {
    metaTitle: 'Peaque — Case Study · ThroughPilot',
    metaDescription:
      'How ThroughPilot designed and built Peaque: a social reading app with a serverless EPUB engine, anchored notes, and community reactions.',
    path: '/Peaque',
    ogImage: '/assets/case-study/peaque/01.jpg',
    kicker: 'Case study · Reading · Social',
    title: (
      <>
        <em>Peaque.io</em>
      </>
    ),
    lede: 'A new way to read — a full ebook reader where the fandom lives right alongside the page. Readers highlight any line to leave a note, and the community celebrates the best ones. Something like a Kindle, Instagram, and Reddit in one app.',
    meta: [
      { label: 'Role', value: 'Product Design & Full-stack' },
      {
        label: 'Status',
        value: (
          <>
            <span className="dot paused" />
            MVP built · launch paused (funding)
          </>
        ),
      },
      { label: 'Service', value: 'Launchpad — MVP build' },
    ],
    chips: [
      { label: 'Serverless EPUB engine', key: true },
      { label: 'Firebase' },
      { label: 'On-demand rendering', key: true },
      { label: 'Highlight-to-note' },
      { label: 'Upvotes · replies · shares', key: true },
      { label: 'Full-text search' },
    ],
    heroImage: {
      src: '/assets/case-study/peaque/01.jpg',
      alt: 'Peaque library — trending classic books with the in-app reader',
    },
    overview: {
      label: 'The idea',
      title: (
        <>
          The book and its <em>fandom,</em> in one place.
        </>
      ),
      body: (
        <>
          <p>
            Readers already live a double life: they read the book in one app,
            then go to Instagram, Reddit, or a fan wiki to find everyone
            else&rsquo;s art, theories, and reactions. Those two worlds never
            share a screen. Peaque closes that gap — it&rsquo;s a full ebook
            reader where the community&rsquo;s content sits right next to the
            passage it&rsquo;s about.
          </p>
          <p>
            It does everything you expect a reader to do —{' '}
            <b>bookmarks, search, full-screen</b> — and then adds <i>notes:</i>{' '}
            highlight any line and leave a thought, a piece of art, or a theory,
            just for yourself or for everyone. Other readers can{' '}
            <b>upvote, reply, and share,</b> so a great note spreads and the
            author&rsquo;s world keeps growing.
          </p>
          <p>
            Under it all sits the part we were proudest of: a{' '}
            <b>first-of-its-kind serverless EPUB engine</b> that parses books and
            renders pages on demand, so any title opens instantly in the browser
            with nothing to install. We designed and built it end to end on
            Firebase — the reading engine, the highlight-anchored note system, and
            the community layer.
          </p>
        </>
      ),
    },
    highlights: {
      title: 'What makes it work.',
      features: [
        {
          num: '01 — Read',
          title: 'Everything you expect, plus the fandom.',
          media: {
            src: '/assets/case-study/peaque/02.webp',
            alt: 'Peaque reader — book text with community notes in a side panel',
          },
          body: (
            <p>
              Bookmarks, full-text search, full-screen — the fundamentals of a
              good reader, tuned so the page is all you see. And as you read, the
              community&rsquo;s notes appear right alongside the page, so a scene
              and the reactions it inspired finally share a screen.
            </p>
          ),
          points: [
            'Bookmarks, progress, and full-screen reading',
            'Full-text search across the whole book',
            'Community notes shown alongside the page',
          ],
        },
        {
          num: '02 — Create',
          title: 'Highlight a line, leave a note.',
          flip: true,
          media: <PeaqueCreate />,
          body: (
            <p>
              Notes are how the fandom shows up on Peaque. Highlight any text and
              add a thought, a piece of art, or a theory — kept private for just
              you, or made public for everyone reading the book. It&rsquo;s the
              lightest possible way to turn a solitary read into a shared one.
            </p>
          ),
          points: [
            'Highlight any passage to attach a note',
            'Private to you, or public to the whole community',
            'Notes stay anchored to the exact lines they’re about',
          ],
        },
        {
          num: '03 — Celebrate',
          title: 'The best notes rise to the top.',
          media: {
            src: '/assets/case-study/peaque/04.webp',
            alt: 'Peaque popular notes — the community’s best notes on a book',
          },
          body: (
            <p>
              As readers add notes, they enrich the author&rsquo;s world and
              create new things for other fans to enjoy. Upvote a note to spark
              joy, reply to start a thread, or share it onward — the community
              decides what&rsquo;s worth seeing, and the best of it travels.
            </p>
          ),
          points: [
            'Upvotes, replies, and shares on every note',
            'The strongest notes surface to other readers',
            'Public content moderated to community guidelines',
          ],
        },
        {
          num: '04 — Serverless by design',
          title: 'A first-of-its-kind EPUB engine.',
          flip: true,
          media: <PeaqueEngine />,
          body: (
            <p>
              An EPUB is really a zipped bundle of HTML, CSS, and assets —
              normally unpacked by a heavy native app. Peaque does it in the cloud
              instead: the book is parsed and each page is rendered on demand, so
              any title opens instantly in the browser with nothing to download.
              The whole thing runs serverless on Firebase, with no servers to
              manage.
            </p>
          ),
          points: [
            'Parses EPUB structure and renders pages on demand',
            'Fully serverless on Firebase — nothing to install or run',
            'Books open instantly, straight in the browser',
          ],
        },
      ],
    },
    hood: {
      title: 'Quiet on the surface, careful underneath.',
      cards: [
        {
          t: 'Platform',
          v: 'Firebase',
          d: (
            <>
              Serverless backend — Firestore, Cloud Functions, Auth &amp; Storage.
            </>
          ),
        },
        {
          t: 'Reading engine',
          v: 'Serverless EPUB',
          d: 'Parses books and renders pages on demand — nothing to install.',
        },
        {
          t: 'Notes',
          v: 'Text-range anchoring',
          d: (
            <>
              Notes pinned to passages, stable across reflow &amp; re-pagination.
            </>
          ),
        },
        {
          t: 'Community',
          v: 'Social graph',
          d: 'Upvotes, replies, shares, and public / private visibility.',
        },
        {
          t: 'Search',
          v: 'Full-text index',
          d: 'Whole-book search that resolves to the exact passage.',
        },
        {
          t: 'Safety',
          v: 'Moderation + DMCA',
          d: 'Community guidelines, reporting, and copyright handling.',
        },
      ],
      note: (
        <>
          The breakthrough was <b>rendering EPUBs serverlessly</b> — parsing a
          zipped book and producing pages on demand in the cloud, so the reader is
          just a browser with nothing to download. The other half was{' '}
          <b>making a note stay attached to the exact words it&rsquo;s about:</b>{' '}
          anchoring user content to text ranges that survive reflow, font changes,
          and re-pagination, with a social layer that stays welcoming and
          moderated as it grows.
        </>
      ),
    },
    outcome: {
      label: 'Outcome',
      title: 'Reading, made social.',
      metrics: [
        { n: 'Serverless', l: 'First-of-its-kind EPUB engine', sm: true },
        {
          n: (
            <>
              0<em>-download</em>
            </>
          ),
          l: 'Books open instantly in the browser',
          sm: true,
        },
        {
          n: (
            <>
              1<em>-highlight</em>
            </>
          ),
          l: 'From passage to public note',
        },
        {
          n: (
            <>
              3<em>-way</em>
            </>
          ),
          l: 'Upvote · reply · share',
        },
      ],
    },
    prev: {
      href: '/1Mind',
      dir: '← Prev case',
      ttl: (
        <>
          1<em>Mind.com</em>
        </>
      ),
    },
    next: {
      href: '/#work',
      dir: 'Back to →',
      ttl: (
        <>
          All <em>work</em>
        </>
      ),
    },
  },
}
